/*
  Derived figures for a report's videos, plus the small parsers the detail pane
  needs for the comma- and pipe-separated fields the API returns.

  MatchingVideos keys a video as VideoId, the playback summary keys it as
  videoId, and the two disagree on type — hence String() on both sides of every
  join.
*/

// MatchingVideos arrives either as a JSON string or already parsed.
export const parseMatchingVideos = (matchingVideos) => {
  if (!matchingVideos) return []
  if (Array.isArray(matchingVideos)) return matchingVideos
  try {
    return JSON.parse(matchingVideos)
  } catch {
    // Warn rather than swallow: an unparseable payload otherwise renders as an
    // ordinary "0 videos", indistinguishable from a genuine zero.
    console.warn("Could not parse MatchingVideos", matchingVideos)
    return []
  }
}

export const parseCsvList = (value) =>
  value
    ? value
        .split(",")
        .map((entry) => entry.trim())
        .filter(Boolean)
    : []

// MatchingPhysicians is "|"-separated, each entry "NPI, Name" — and a name can
// itself contain commas, so only the first one separates the two.
export const parsePhysicians = (matchingPhysicians) =>
  (matchingPhysicians ? matchingPhysicians.split("|") : [])
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const commaIndex = entry.indexOf(",")
      return commaIndex === -1
        ? { npi: entry, name: "" }
        : {
            npi: entry.slice(0, commaIndex).trim(),
            name: entry.slice(commaIndex + 1).trim(),
          }
    })

export const buildPlaybackIndex = (playbackSummary) =>
  (playbackSummary || []).reduce((byId, playback) => {
    byId[String(playback.videoId)] = playback
    return byId
  }, {})

/*
  How far the patient got with one video.

  A null maxPercent means the video was never played, which is not the same as
  having played it and reached 0% — the second is real engagement data, so it
  keeps a normal title and an empty bar rather than the muted "not viewed"
  treatment.

  maxPercent originates from ViewMedica's "50%" strings, so it is clamped; a
  fractional or out-of-range value would otherwise size the bar wrongly.
*/
export const getVideoProgress = (playback) => {
  const raw = playback?.maxPercent ?? null
  const isViewed = raw !== null
  const percent = isViewed ? Math.min(100, Math.max(0, Math.round(raw))) : 0
  const watchCount = playback?.watchCount ?? 0

  return {
    isViewed,
    percent,
    // video_ended can fire without a 100% milestone, so `completed` counts too.
    isComplete: percent >= 100 || playback?.completed === true,
    watchCount,
    // A single watch is already implied by the percentage, so only a repeat is
    // worth remarking on. watchCount counts restarts, which means seeking
    // backwards reads as another watch — the player gives no signal that
    // separates the two.
    isRewatched: watchCount > 1,
  }
}

/*
  Coverage across a report's videos: how many were opened, and how far the
  patient got in the ones they did open.

  Iterates the assigned videos and looks playback up, never the reverse — the
  playback summary can still hold a video since removed from the report, which
  counted the other way around would report "6 of 5".

  The average spans viewed videos only. Averaging over all of them (unviewed as
  0%) conflates "never opened" with "abandoned immediately", and makes the figure
  drop when a video is added to a report — an engagement regression that never
  happened. The "N of M" half already carries coverage; the percentage describes
  depth.
*/
export const summariseVideos = (videos, playbackByVideoId) => {
  const viewed = videos
    .map((video) => getVideoProgress(playbackByVideoId[String(video?.VideoId)]))
    .filter((progress) => progress.isViewed)

  const averagePercent = viewed.length
    ? Math.round(
        viewed.reduce((sum, progress) => sum + progress.percent, 0) /
          viewed.length
      )
    : null

  return { total: videos.length, viewedCount: viewed.length, averagePercent }
}
